import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Divider,
    Grid2,
    Button,
    useTheme,
    alpha
} from "@mui/material";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SingleFileUpload from "../../../fileUpload/FileUpload";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { updateProjectDetails, fetchProjectDetails } from "../../../../store/startup/aboutProduct/projectDetailsSlice";

const ProjectDetails = () => {
    const [value, setValue] = useState('');
    const dispatch = useDispatch();
    const { startupId } = useParams();

    const { projectDetails, loading, error } = useSelector((state) => state.projectDetails);

    const [summary, setSummary] = useState("");
    const [details, setDetails] = useState("");
    const [problemDescription, setProblemDescription] = useState("");
    const [solutionDescription, setSolutionDescription] = useState("");
    const [valuePropositions, setValuePropositions] = useState("");
    const [relatedDocumentPath, setRelatedDocumentPath] = useState("");

    // Add validation state
    const [errors, setErrors] = useState({});

    const theme = useTheme();
    
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['clean']
        ]
    };

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet'
    ];

    // Sayfa yüklendiğinde verileri çek
    useEffect(() => {
        if (startupId) {
            dispatch(fetchProjectDetails(startupId));
        }
    }, [dispatch, startupId]);

    // Debug için log ekleyelim
    useEffect(() => {
        console.log("Project Details:", projectDetails);
    }, [projectDetails]);

    // Veriler geldiğinde state'leri güncelle
    useEffect(() => {
        if (projectDetails) {
            setSummary(projectDetails.summary || "");
            setDetails(projectDetails.details || "");
            setProblemDescription(projectDetails.problemDescription || "");
            setSolutionDescription(projectDetails.solutionDescription || "");
            setValuePropositions(projectDetails.valuePropositions || "");
            setRelatedDocumentPath(projectDetails.relatedDocumentPath || "");
            
            // Log for debugging
            console.log("Related Document Path:", projectDetails.relatedDocumentPath);
        }
    }, [projectDetails]);

    // Add validation function
    const validateForm = () => {
        const newErrors = {};
        
        if (!summary.trim()) {
            newErrors.summary = "Ürün ve Teknoloji özeti zorunludur";
        }
        if (!details.trim()) {
            newErrors.details = "Ürün/Proje detayı zorunludur";
        }
        if (!problemDescription.trim()) {
            newErrors.problemDescription = "Sorun açıklaması zorunludur";
        }
        if (!solutionDescription.trim()) {
            newErrors.solutionDescription = "Çözüm açıklaması zorunludur";
        }
        if (!valuePropositions.trim()) {
            newErrors.valuePropositions = "Değer önerileri zorunludur";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validateForm()) {
            return;
        }

        const updatedInfo = {
            startupId,
            summary,
            details,
            problemDescription,
            solutionDescription,
            valuePropositions,
            relatedDocumentPath,
        };
        
        console.log("Saving with document path:", relatedDocumentPath);
        dispatch(updateProjectDetails(updatedInfo));
    };

    return (
        <Box sx={{ 
            margin: "0 auto", 
            maxWidth: 900,
            '& .quill': {
                '& .ql-toolbar': {
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                    borderTopLeftRadius: theme.shape.borderRadius,
                    borderTopRightRadius: theme.shape.borderRadius,
                    backgroundColor: theme.palette.mode === 'light' 
                        ? alpha(theme.palette.grey[100], 0.5)
                        : alpha(theme.palette.grey[800], 0.5),
                    '& .ql-stroke': {
                        stroke: theme.palette.mode === 'light' 
                            ? theme.palette.grey[700]
                            : theme.palette.grey[300]
                    },
                    '& .ql-fill': {
                        fill: theme.palette.mode === 'light' 
                            ? theme.palette.grey[700]
                            : theme.palette.grey[300]
                    },
                    '& .ql-picker': {
                        color: theme.palette.text.primary
                    },
                    '& .ql-picker-options': {
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                        boxShadow: theme.shadows[3]
                    }
                },
                '& .ql-container': {
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                    borderBottomLeftRadius: theme.shape.borderRadius,
                    borderBottomRightRadius: theme.shape.borderRadius,
                    backgroundColor: theme.palette.background.paper
                },
                '& .ql-editor': {
                    minHeight: '200px',
                    fontFamily: theme.typography.fontFamily,
                    fontSize: theme.typography.body1.fontSize,
                    color: theme.palette.text.primary,
                    '&.ql-blank::before': {
                        color: theme.palette.text.disabled,
                        fontStyle: 'normal',
                        content: 'attr(data-placeholder)'
                    }
                }
            }
        }}>
            <Typography variant="h5" sx={{ marginTop: 1 }} gutterBottom>
                Ürün/Proje Hakkında
            </Typography>
            <Divider sx={{ marginBottom: 3, bgcolor: 'black' }} />

            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Ürün ve Teknoloji (Kısa Özeti)
                </Typography>
                <ReactQuill 
                    theme="snow" 
                    value={summary} 
                    onChange={setSummary}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Ürün ve teknoloji hakkında kısa özet giriniz..."
                />
                {errors.summary && (
                    <Typography color="error" variant="caption">
                        {errors.summary}
                    </Typography>
                )}
            </Box>

            <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 8 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Ürün/Proje Hakkında
                        </Typography>
                        <ReactQuill 
                            theme="snow" 
                            value={details} 
                            onChange={setDetails}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Ürün/proje detaylarını giriniz..."
                        />
                        {errors.details && (
                            <Typography color="error" variant="caption">
                                {errors.details}
                            </Typography>
                        )}
                    </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }} >
                        <SingleFileUpload 
                            onFileUpload={(path) => setRelatedDocumentPath(path)}
                            onUploadComplete={(path) => setRelatedDocumentPath(path)}
                            existFilePath={relatedDocumentPath}
                            fileUploadPath="startup"
                        />
                    </Box>
                </Grid2>
            </Grid2>

            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Tespit Ettiğin Sorun
                </Typography>
                <ReactQuill 
                    theme="snow" 
                    value={problemDescription} 
                    onChange={setProblemDescription}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Tespit ettiğiniz sorunu açıklayınız..."
                />
                {errors.problemDescription && (
                    <Typography color="error" variant="caption">
                        {errors.problemDescription}
                    </Typography>
                )}
            </Box>

            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Bulduğun Çözüm ve Teknoloji
                </Typography>
                <ReactQuill 
                    theme="snow" 
                    value={solutionDescription} 
                    onChange={setSolutionDescription}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Çözümünüzü ve kullandığınız teknolojiyi açıklayınız..."
                />
                {errors.solutionDescription && (
                    <Typography color="error" variant="caption">
                        {errors.solutionDescription}
                    </Typography>
                )}
            </Box>

            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Değer Önerileri
                </Typography>
                <ReactQuill 
                    theme="snow" 
                    value={valuePropositions} 
                    onChange={setValuePropositions}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Değer önerilerinizi açıklayınız..."
                />
                {errors.valuePropositions && (
                    <Typography color="error" variant="caption">
                        {errors.valuePropositions}
                    </Typography>
                )}
            </Box>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                    variant="contained" 
                    color="primary"
                    onClick={handleSave}
                    disabled={loading}
                >
                    {loading ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
            </Box>
        </Box>
    );
};

export default ProjectDetails;